import React from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'text';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  href?: string;
  to?: string;
  external?: boolean;
}

const variantClasses = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600 active:bg-indigo-700',
  secondary: 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100',
  danger: 'bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-600 active:bg-red-700',
  text: 'bg-transparent text-gray-700 hover:text-gray-900 focus-visible:outline-none active:text-gray-800',
};

const sizeClasses = {
  small: 'px-4 py-3 text-base',
  medium: 'px-6 py-4 text-lg',
  large: 'px-8 py-5 text-xl',
};

const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 min-h-[56px] min-w-[56px]';

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  href,
  to,
  external,
  className = '',
  disabled,
  onClick,
  ...props
}) => {
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${
    fullWidth ? 'w-full' : ''
  } ${className}`;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || isLoading) {
      e.preventDefault();
      return;
    }
    
    if (onClick) {
      onClick(e as any);
    }
  };

  const commonProps = {
    className: classes,
    onClick: handleClick,
    style: {
      WebkitTapHighlightColor: 'transparent',
      WebkitTouchCallout: 'none',
      touchAction: 'manipulation',
      userSelect: 'none',
      cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
      '-webkit-user-select': 'none',
      '-moz-user-select': 'none',
      '-ms-user-select': 'none',
      outline: 'none',
    } as React.CSSProperties,
    role: 'button',
    'aria-disabled': disabled || isLoading,
    disabled: disabled || isLoading,
  };

  const content = (
    <>
      {isLoading && (
        <LoadingSpinner
          size="small"
          className={children ? 'mr-2' : ''}
        />
      )}
      {!isLoading && icon && iconPosition === 'left' && (
        <span className={`${children ? 'mr-2' : ''}`}>{icon}</span>
      )}
      {children}
      {!isLoading && icon && iconPosition === 'right' && (
        <span className={`${children ? 'ml-2' : ''}`}>{icon}</span>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        {...commonProps}
      >
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        {...commonProps}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      {...commonProps}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button; 